import { useCallback } from 'react';
import Commons from '../Services/Common/Common';
import { showSnackbar } from '../Reducer/SnackbarReducer';
import { useDispatch } from 'react-redux';
import { setChanged } from '../Reducer/GridReducer';

// useDelete - 그리드에서 삭제 관련 로직을 공통으로 관리하는 훅
// - 삭제 예정 표시(MarkedForDeletion)
// - 삭제 복원(RestoreRow)
// - 실제 삭제(DeleteLogic) 처리까지 포함 (payload필요)
//
// 파라미터 설명:
// - gridRef: AG Grid의 ref
// - gridState: grid 상태 객체 (deleted 등)
// - getDeletePayload: 삭제 API에 넘길 payload 구성 함수
// - updateDeleted: 삭제된 행 목록을 업데이트하는 함수
// - updateRowData: 실제 삭제된 후 최종 rowData를 갱신할 함수
// - setChangedRowData1: 추가된 행 구분 및 DeleteNewRow에서 필요
//
// 사용법:
//  const { MarkedForDeletion, RestoreRow, DeleteLogic } = useDelete({
//    gridRef: gridRef1,
//    gridState: gridState1,
//    getDeletePayload,
//    updateDeleted: setDeletedRowData1,
//    updateRowData: setRowData1,
//    setChangedRowData: setChangedRowData1,
//  });

export default function useDelete({
  gridRef,
  gridState,
  getDeletePayload,
  getDeleteUrl,
  updateDeleted,
  updateRowData,
  deletedRowData = [],
  rowData = [],
  setChangedRowData,
}) {
  const dispatch = useDispatch();

  const getDeleted = useCallback(() => {
    return gridState?.deleted.length ? gridState?.deleted : deletedRowData;
  }, [gridState, deletedRowData]);

  // 삭제 예정 행 표시
  const MarkedForDeletion = useCallback(
    (rows) => {
      if (!rows || rows.length === 0) {
        dispatch(
          showSnackbar({
            message: '삭제할 행을 선택하세요.',
            type: 'warning',
          })
        );
        return;
      }

      const api = gridRef.current.api;
      const deleted = [...getDeleted()];

      const updatedRows = rows.map((row) => {
        const rowNode = api.getRowNode(row.no);
        const originalIndex = rowNode?.rowIndex ?? 0;

        return {
          ...row,
          deleteYn: 'Y',
          originalIndex,
        };
      });

      updatedRows.forEach((row) => {
        api.applyTransaction({ remove: [row] });
        api.applyTransaction({ add: [row], addIndex: 0 });
      });

      const updatedDeleted = [
        ...deleted,
        ...updatedRows.filter((r) => !deleted.some((d) => d.no === r.no)),
      ];

      updateDeleted?.(updatedDeleted);

      if (gridState) {
        const result = [
          ...updatedRows.reverse().map((r) => ({
            ...r,
            deleteYn: 'Y',
          })),
          ...gridState.rowData.filter(
            (p) => !updatedRows.some((r) => r.no === p.no)
          ),
        ];
        dispatch(
          setChanged({
            gridId: 'grid1',
            rowData: result,
          })
        );

        // dispatch(
        //   setRowData({
        //     gridId: 'grid1',
        //     rowData: result,
        //   })
        // );
      } else {
        setChangedRowData((prev) => {
          return [
            ...prev.filter((p) => !updatedRows.some((r) => r.no === p.no)),
            ...updatedRows.map((r) => ({
              ...r,
              deleteYn: 'Y',
            })),
          ];
        });
      }
    },
    [getDeleted, gridRef, updateDeleted]
  );

  // 삭제 복원
  const RestoreRow = useCallback(
    (row) => {
      const api = gridRef.current.api;
      const restored = { ...row };
      delete restored.deleteYn;
      delete restored.originalIndex;
      let insertIndex = 0;
      const rowCount = api.getDisplayedRowCount();
      let addRowCount = 0;

      if (rowCount > 0) {
        api.forEachNode((node) => {
          // 신규행 카운트
          if (node.data.newYn) {
            ++addRowCount;
          }
          // 삭제예정, 신규행 제외하고 그리드 최상단행의 index - 1
          if (
            insertIndex === 0 &&
            node.data?.deleteYn !== 'Y' &&
            node.data?.newYn !== 'Y'
          ) {
            insertIndex = node.rowIndex - 1;
          }
        });
      }

      if (rowCount === getDeleted().length + addRowCount) {
        insertIndex = getDeleted().length - 1 + addRowCount;
      } else if (getDeleted().length === 1) {
        insertIndex = 0 + addRowCount;
      }

      api.applyTransaction({ remove: [row] });
      api.applyTransaction({ add: [restored], addIndex: insertIndex });

      const filteredDeleted = getDeleted()
        .filter((r) => r.no !== row.no)
        .sort((a, b) => b.originalIndex - a.originalIndex);

      updateDeleted?.(filteredDeleted);
    },
    [getDeleted, gridRef, updateDeleted]
  );

  // 실제 삭제 실행
  const DeleteLogic = useCallback(async () => {
    if (!getDeleted().length) {
      return { message: '', severity: 'success', detailData: null };
    }

    const deletePayload = getDeletePayload(getDeleted());
    try {
      const result = await Commons.Axios('post', getDeleteUrl, deletePayload);

      // 백엔드 응답 형식이 SaveLogic과 동일하다고 가정
      // { isSuccess, message, severity, data } 또는 기존 형식 지원
      const apiRes = result.data;

      // 기존 형식 지원 (배열로 오는 경우)
      if (Array.isArray(apiRes)) {
        const firstItem = apiRes[0];
        if (firstItem?.type === 'Error') {
          const msg = firstItem.message || '삭제 실패';
          throw new Error(msg);
        }
        // 기존 형식에서는 상세 데이터 없음
        const gridDeleteRows = rowData.filter(
          (row) => !getDeleted().some((del) => del.no === row.no)
        );
        updateRowData?.(gridDeleteRows);
        return {
          message: '삭제되었습니다.',
          severity: 'success',
          detailData: null,
        };
      }

      // 새로운 형식 (SaveLogic과 동일)
      const { isSuccess, message, severity, data } = apiRes || {};
      const rows = Array.isArray(data) ? data : [];

      // 실패 처리
      if (!isSuccess && severity === 'error') {
        throw new Error(message || '삭제 실패');
      }

      // 성공 시 그리드 업데이트
      const gridDeleteRows = rowData.filter(
        (row) => !getDeleted().some((del) => del.no === row.no)
      );
      updateRowData?.(gridDeleteRows);

      return {
        message: message || '삭제되었습니다.',
        severity: severity || 'success',
        detailData: rows.length > 0 ? rows : null,
      };
    } catch (err) {
      console.error(err);
      throw err;
    }
  }, [getDeleted, getDeletePayload, rowData, updateRowData]);

  // 신규행 제거
  const DeleteNewRow = useCallback(
    (row) => {
      const api = gridRef.current.api;

      if (!row?.no) return;
      api.applyTransaction({ remove: [row] });

      if (gridState) {
        const changedResult = gridState.changed.filter((r) => r.no !== row.no);

        dispatch(
          setChanged({
            gridId: 'grid1',
            rowData: changedResult,
          })
        );
      } else {
        setChangedRowData((prev) => prev.filter((r) => r.no !== row.no));
      }
    },
    [gridRef, setChangedRowData]
  );
  return {
    MarkedForDeletion,
    RestoreRow,
    DeleteLogic,
    DeleteNewRow,
  };
}
